import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  Disclosure,
  Input,
  Label,
  Select,
  buttonVariants,
} from "@safeplate/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, Pencil, Plus, Printer } from "lucide-react";
import { useId, useState, type FormEvent } from "react";

import { displayError } from "../api/errors.js";
import { EmptyState, ErrorState, LoadingState } from "../components/states.js";
import { assetLabelPath, locationLabelsPath } from "../labels/qr-path.js";
import {
  archiveSetupItem,
  formPayload,
  listSetupItems,
  saveSetupItem,
} from "./setup-api.js";
import {
  itemSummary,
  itemTitle,
  labelFor,
  resourceValues,
  visibleOptions,
  type SetupField,
  type SetupItem,
  type SetupResource,
} from "./resources.js";

function fieldValue(
  item: SetupItem | undefined,
  field: SetupField,
): boolean | number | string {
  const value = item?.[field.key] ?? field.defaultValue ?? "";
  if (["string", "number", "boolean"].includes(typeof value)) {
    return value as boolean | number | string;
  }
  if (field.key === "goods_summary" && Array.isArray(item?.locations)) {
    const first = item.locations[0];
    if (
      typeof first === "object" &&
      first !== null &&
      "goods_summary" in first
    ) {
      return typeof first.goods_summary === "string" ? first.goods_summary : "";
    }
  }
  return "";
}

function FieldControl({
  field,
  item,
}: {
  field: SetupField;
  item?: SetupItem;
}) {
  const id = useId();
  const value = fieldValue(item, field);

  if (field.type === "checkbox") {
    return (
      <label className="flex min-h-11 cursor-pointer items-center gap-3 rounded-xl border border-stone-200 bg-stone-50 px-3.5 py-2.5 sm:col-span-2">
        <input
          className="size-4 accent-emerald-800"
          defaultChecked={Boolean(value)}
          name={field.key}
          type="checkbox"
        />
        <span className="text-sm font-semibold text-stone-900">
          {field.label}
        </span>
      </label>
    );
  }

  return (
    <div className={field.type === "textarea" ? "sm:col-span-2" : ""}>
      <Label htmlFor={id}>{field.label}</Label>
      {field.type === "select" ? (
        <Select
          className="mt-1.5"
          defaultValue={String(value)}
          id={id}
          name={field.key}
          required={field.required}
        >
          {!field.required || !value ? <option value="">None</option> : null}
          {visibleOptions(field, value).map((entry) => (
            <option key={entry.value} value={entry.value}>
              {entry.label}
            </option>
          ))}
        </Select>
      ) : field.type === "textarea" ? (
        <textarea
          className="mt-1.5 min-h-24 w-full resize-y rounded-xl border border-stone-300 bg-surface px-3.5 py-3 text-sm shadow-xs outline-none placeholder:text-stone-400 focus:border-emerald-700 focus:ring-3 focus:ring-emerald-700/12"
          defaultValue={String(value)}
          id={id}
          name={field.key}
          placeholder={field.placeholder}
          required={field.required}
        />
      ) : (
        <Input
          className="mt-1.5"
          defaultValue={String(value)}
          id={id}
          name={field.key}
          placeholder={field.placeholder}
          required={field.required}
          step={field.type === "number" ? "any" : undefined}
          type={field.type ?? "text"}
        />
      )}
    </div>
  );
}

function Editor({
  fields,
  item,
  onClose,
  onSubmit,
  resource,
  saving,
  error,
}: {
  error: unknown;
  fields: readonly SetupField[];
  item?: SetupItem;
  onClose: () => void;
  onSubmit: (form: FormData) => void;
  resource: SetupResource;
  saving: boolean;
}) {
  const formId = useId();
  const main = fields.filter((field) => !field.more);
  const more = fields.filter((field) => field.more);
  const controls = (list: readonly SetupField[]) =>
    list.map((field) => (
      <FieldControl
        field={field}
        key={field.key}
        {...(item === undefined ? {} : { item })}
      />
    ));

  return (
    <Dialog
      footer={
        <>
          <Button disabled={saving} onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button disabled={saving} form={formId} type="submit">
            {saving ? "Saving…" : "Save"}
          </Button>
        </>
      }
      onClose={onClose}
      open
      size="lg"
      title={item ? `Edit ${resource.noun}` : resource.addLabel}
    >
      <form
        id={formId}
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          onSubmit(new FormData(event.currentTarget));
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">{controls(main)}</div>
        {more.length > 0 ? (
          <Disclosure className="mt-4" summary="More details">
            <div className="grid gap-4 pt-2 sm:grid-cols-2">
              {controls(more)}
            </div>
          </Disclosure>
        ) : null}
        {error ? (
          <p
            className="mt-5 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-800"
            role="alert"
          >
            {displayError(error)}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}

export function ResourcePanel({
  areaOptions = [],
  canManage,
  locationId,
  resource,
}: {
  areaOptions?: readonly { label: string; value: string }[];
  canManage: boolean;
  locationId: string;
  resource: SetupResource;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<SetupItem | "new" | null>(null);
  const queryKey = ["setup", resource.key, locationId];
  const query = useQuery({
    queryFn: () => listSetupItems(resource, locationId),
    queryKey,
  });
  const save = useMutation({
    mutationFn: ({ form, item }: { form: FormData; item?: SetupItem }) =>
      saveSetupItem(
        resource,
        locationId,
        resourceValues(resource, formPayload(resource.fields, form, item)),
        item,
      ),
    onSuccess: async () => {
      setEditing(null);
      await queryClient.invalidateQueries({ queryKey });
      if (resource.key === "documents") {
        await queryClient.invalidateQueries({
          queryKey: ["setup", "reminders", locationId],
        });
      }
    },
  });
  const archive = useMutation({
    mutationFn: (item: SetupItem) => archiveSetupItem(resource, item),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey }),
  });
  const fields = resource.fields
    .filter((field) => field.key !== "area_id" || areaOptions.length > 0)
    .map((field) =>
      field.key === "area_id" ? { ...field, options: areaOptions } : field,
    );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
        <CardTitle>{resource.title}</CardTitle>
        {canManage && query.data && query.data.length > 0 ? (
          <div className="flex shrink-0 gap-2">
            {resource.key === "assets" ? (
              <a
                aria-label="Print all QR labels"
                className={buttonVariants({ size: "sm", variant: "outline" })}
                href={locationLabelsPath(locationId)}
                rel="noreferrer"
                target="_blank"
              >
                <Printer className="size-4" />{" "}
                <span className="hidden sm:inline">QR labels</span>
              </a>
            ) : null}
            <Button
              aria-label={resource.addLabel}
              onClick={() => setEditing("new")}
              size="sm"
              variant="outline"
            >
              <Plus className="size-4" />{" "}
              <span className="hidden sm:inline">{resource.addLabel}</span>
            </Button>
          </div>
        ) : null}
      </CardHeader>
      <CardContent className="pt-3">
        {query.isPending ? (
          <LoadingState label={`Loading ${resource.title.toLowerCase()}`} />
        ) : null}
        {query.error ? (
          <ErrorState
            message={displayError(query.error)}
            onAction={() => void query.refetch()}
            title={`Couldn’t load ${resource.title.toLowerCase()}`}
          />
        ) : null}
        {query.data?.length === 0 ? (
          <EmptyState
            action={
              canManage ? (
                <Button onClick={() => setEditing("new")}>
                  <Plus className="size-4" /> {resource.addLabel}
                </Button>
              ) : undefined
            }
            title={resource.emptyLabel ?? `Add your first ${resource.noun}`}
          />
        ) : null}
        {query.data && query.data.length > 0 ? (
          <div className="-mx-5 -mb-5 divide-y divide-stone-200 border-t border-stone-200">
            {query.data.map((item) => (
              <div
                className="flex flex-col gap-2 px-5 py-3 sm:flex-row sm:items-center"
                key={item.id}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate text-sm font-bold text-stone-950">
                      {itemTitle(resource, item)}
                    </p>
                    {typeof item.status === "string" &&
                    item.status !== "active" &&
                    item.status !== "ok" ? (
                      <Badge
                        tone={item.status === "unsafe" ? "danger" : "neutral"}
                      >
                        {labelFor(item.status)}
                      </Badge>
                    ) : null}
                    {typeof item.proof_status === "string" ? (
                      <Badge
                        tone={
                          item.proof_status === "proven"
                            ? "positive"
                            : "attention"
                        }
                      >
                        {labelFor(item.proof_status)}
                      </Badge>
                    ) : null}
                  </div>
                  {itemSummary(resource, item) ? (
                    <p className="mt-1 truncate text-xs text-stone-500">
                      {itemSummary(resource, item)}
                    </p>
                  ) : null}
                </div>
                {canManage ? (
                  <div className="flex shrink-0 flex-wrap gap-1">
                    {resource.key === "assets" ? (
                      <a
                        className={buttonVariants({
                          size: "sm",
                          variant: "ghost",
                        })}
                        href={assetLabelPath(item.id)}
                        rel="noreferrer"
                        target="_blank"
                      >
                        <Printer className="size-3.5" /> QR label
                      </a>
                    ) : null}
                    <Button
                      onClick={() => setEditing(item)}
                      size="sm"
                      variant="ghost"
                    >
                      <Pencil className="size-3.5" /> Edit
                    </Button>
                    <Button
                      disabled={archive.isPending}
                      onClick={() => {
                        if (
                          globalThis.confirm(
                            `${resource.archiveLabel ?? "Archive"} this ${resource.noun}?`,
                          )
                        )
                          archive.mutate(item);
                      }}
                      size="sm"
                      variant="ghost"
                    >
                      <Archive className="size-3.5" />{" "}
                      {resource.archiveLabel ?? "Archive"}
                    </Button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
        {archive.error ? (
          <p className="mt-3 text-sm font-semibold text-red-800" role="alert">
            {displayError(archive.error)}
          </p>
        ) : null}
      </CardContent>
      {editing ? (
        <Editor
          error={save.error}
          fields={fields}
          {...(editing === "new" ? {} : { item: editing })}
          onClose={() => {
            save.reset();
            setEditing(null);
          }}
          onSubmit={(form) =>
            save.mutate({
              form,
              ...(editing === "new" ? {} : { item: editing }),
            })
          }
          resource={resource}
          saving={save.isPending}
        />
      ) : null}
    </Card>
  );
}
